import { useCallback, useEffect, useState } from "react";
import {
  PartFieldsFragment,
  TerminFieldsFragment,
  TerminUpdateInput,
  useCreateMeetingMutation,
  useFindMeetingQuery,
  useRequestChatTokenMutation,
  useUpdateMeetingMutation,
} from "../../generated/platform";

export enum ViewAs {
  Expert,
  ActiveParticipant,
  Participant,
  Forbidden,
  Unknown,
}

export const useRequestMeeting = () => {
  const [
    createTerminMutation,
    { data, loading, error },
  ] = useCreateMeetingMutation();
  return {
    loading,
    error,
    data,
    request: (
      classificationId: number,
      participantId: number,
      expertId: number,
      expoCode: string,
      corpId: number
    ) => {
      const startDate = new Date();
      const endDate = new Date(startDate.getTime() + 30 * 60000);
      return createTerminMutation({
        variables: {
          input: {
            corporation: corpId,
            product: classificationId,
            expo: expoCode,
            hasEnded: false,
            meetingType: "specific",
            expert: expertId,
            participant: participantId,
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            status: "open",
          },
        },
      });
    },
  };
};

export const useMeeting = (uuid: string, showError: (raw?: any) => void) => {
  const [meeting, setMeeting] = useState<TerminFieldsFragment | undefined>();
  const { data, loading, error } = useFindMeetingQuery({
    variables: {
      uuid,
    },
  });

  useEffect(() => {
    if (!loading && data?.meeting) {
      setMeeting(data.meeting);
    }
    if (!loading && error) {
      showError(error);
    }
  }, [loading, data, error, showError]);

  return {
    meeting,
    setMeeting,
    loading,
  };
};

export const useRequestChatToken = (showError: (raw?: any) => void) => {
  const [
    requestChatTokenMutation,
    { loading, error },
  ] = useRequestChatTokenMutation();

  useEffect(() => {
    if (!loading && error) {
      showError(error);
    }
  }, [loading, error, showError]);

  return {
    loading,
    request: useCallback(
      (partId: number, uuid: string) => {
        return requestChatTokenMutation({
          variables: {
            partId,
            uuid,
          },
        });
      },
      [requestChatTokenMutation]
    ),
  };
};

export const useUpdateMeeting = () => {
  const [updateMeetingMutation] = useUpdateMeetingMutation();

  return {
    update: (id: number, input: TerminUpdateInput) => {
      return updateMeetingMutation({
        variables: {
          id,
          input,
        },
      });
    },
  };
};

export const useViewAs = (
  user?: PartFieldsFragment,
  meeting?: TerminFieldsFragment
) => {
  const [viewAs, setViewAs] = useState<ViewAs>(ViewAs.Unknown);

  useEffect(() => {
    if (!user || !meeting?.chat) {
      return;
    }
    if (user?.partId === meeting?.participant?.partId) {
      setViewAs(ViewAs.ActiveParticipant);
      return;
    }

    if (user?.partId === meeting?.expert?.partId) {
      setViewAs(ViewAs.Expert);
      return;
    }

    if (
      user &&
      meeting?.participants &&
      meeting?.participants.filter((p) => user.partId === p?.partId).length > 0
    ) {
      setViewAs(ViewAs.Participant);
      return;
    }

    setViewAs(ViewAs.Forbidden);
  }, [user, meeting, setViewAs]);

  return viewAs;
};
